/**
 * Швидке створення (DAT-01): стан відкритого діалогу й передача введеного в повну форму (DAT-02).
 * Модуль крихітний і лежить у головному чанку: «+» верхньої панелі, `N`, палітра й «Створити …» у
 * списку лише викликають `openQuickCreate`, а сам діалог (`create/QuickCreateDialog.tsx`, з формами
 * й стилями) вантажиться ліниво — наперед у простої і при відкритті (`quick-create-load.ts`).
 * Модуль не імпортує `create/*` навіть динамічно: його бере `sign-out.ts`, а діалог через `meta.ts`
 * сам веде до `sign-out.ts` — ребро звідси замкнуло б цикл (`no-circular`, dependency-cruiser).
 *
 * Передача в повну форму — у пам'яті вкладки, а не в адресі чи стані історії: значення полів (ПД)
 * не мають потрапити в історію браузера; поля `sensitive` у сховище вкладки не йдуть (чернетки, #233).
 */
import type { FieldValue } from '@clientline/contracts';
import { useSyncExternalStore } from 'react';

export interface QuickCreateRequest {
  /** Сутність з контексту (список, «+» блоку пов'язаних); без неї — останній обраний тип. */
  entity?: string;
  /**
   * Значення полів з контексту — лише для полів форми: господар запису («+ <зобов'язання>» у вкладці
   * грошей, Б5-W1) або набране в палітрі на порожньому результаті (номер, пошта, назва — SRC-14). Це
   * буває ПД: лише в пам'яті вкладки, як і сам запит, і скидається на виході (`sign-out.ts`).
   */
  preset?: Record<string, FieldValue>;
}

let request: QuickCreateRequest | null = null;
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};

export function openQuickCreate(from: QuickCreateRequest = {}): void {
  request = { ...from };
  emit();
}

export function closeQuickCreate(): void {
  if (request === null) return;
  request = null;
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useQuickCreateRequest(): QuickCreateRequest | null {
  return useSyncExternalStore(
    subscribe,
    () => request,
    () => null,
  );
}

type Values = Record<string, FieldValue>;
const handoffs = new Map<string, Values>();

/** «Більше полів»: введене у швидкому створенні — у повну форму сутності, один раз. */
export function handOff(entity: string, values: Values): void {
  handoffs.set(entity, values);
}

export function takeHandoff(entity: string): Values | undefined {
  const values = handoffs.get(entity);
  handoffs.delete(entity);
  return values;
}

/** Вихід: передане, але ще не взяте, не переживає сесію людини. */
export function clearHandoffs(): void {
  handoffs.clear();
}
